import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";
import Loading from "./Loading";
import "./loader.css";
import "./AtaCard.css";
import AtaObjetivos from "./AtaObjetivos";
import AtaPdf from "./AtaPdf";
import AtaTextoEditor from "./AtaTextoEditor";
import { FaTimes, FaUserPlus, FaChevronLeft, FaChevronRight, FaTasks, FaPencilAlt, FaEnvelope, FaExpand, FaCompress } from "react-icons/fa";
import EnviarAtaEmailModal from "./EnviarAtaEmailModal";

const formatarNomeExibicao = (nomeCompleto) => {
  if (!nomeCompleto || typeof nomeCompleto !== 'string') return "";
  const palavras = nomeCompleto.trim().split(/\s+/);
  return palavras.slice(0, 2)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
};

// ── Chaves para salvar as preferências no localStorage ──
const getStorageKey = (notaId) => `ata_mostrar_objetivos_${notaId}`;
const getStorageKeyTexto = (notaId) => `ata_mostrar_texto_${notaId}`;

export default function AtaCard({
  projetoAtual,
  notaAtual,
  ultimaAlteracao,
  onProgressoChange,
  containerAtual,
  onClose,
  onOpenTask,
  atasDaPilha = [],
  indexAtual = -1,
  onNavegarAta = null,
}) {
  const [projetoNome, setProjetoNome]           = useState("");
  const [pauta, setPauta]                       = useState("");
  const [local, setLocal]                       = useState("");
  const [texto, setTexto]                       = useState("");
  const [proxima, setProxima]                   = useState("");
  const [participantes, setParticipantes]       = useState([]);
  const [participanteInput, setParticipanteInput] = useState("");
  const [sugestoesParticipantes, setSugestoesParticipantes] = useState([]);
  const [autorNome, setAutorNome]               = useState("Carregando...");
  const [usuarioId, setUsuarioId]               = useState(null);
  const [ataId, setAtaId]                       = useState(null);
  const [editing, setEditing]                   = useState({ pauta: false, local: false });
  const [editingDataLocal, setEditingDataLocal] = useState(false);
  const [dataLocal, setDataLocal]               = useState("");
  const [loading, setLoading]                   = useState(true);
  const [extIdCounter, setExtIdCounter]         = useState(0);
  const [alteradoPorNome, setAlteradoPorNome]   = useState("");
  const [alteradoEm, setAlteradoEm]             = useState("");
  const [salvando, setSalvando]                 = useState(false);
  const [salvoComSucesso, setSalvoComSucesso]   = useState(false);
  const [pdfDropdownOpen, setPdfDropdownOpen]   = useState(false);
  const [showParticipanteInput, setShowParticipanteInput] = useState(false);
  const [showEnviarEmail, setShowEnviarEmail] = useState(false);
  const [telaCheia, setTelaCheia] = useState(false);

  // ── States para observação ──
  const [observacao, setObservacao] = useState("");
  const [mostrarObservacaoInput, setMostrarObservacaoInput] = useState(false);
  const observacaoRef = useRef(null);

  // ── States adicionais para observação ──
  const [observacaoDraft, setObservacaoDraft] = useState("");
  const [observacaoEditando, setObservacaoEditando] = useState(true);

  // ── Estado para controlar a exibição dos objetivos (com persistência) ──
  const [mostrarObjetivos, setMostrarObjetivos] = useState(() => {
    if (notaAtual?.id) {
      const salvo = localStorage.getItem(getStorageKey(notaAtual.id));
      return salvo === null ? false : salvo === 'true';
    }
    return false;
  });

  // ── Estado para controlar a exibição do texto (com persistência) ──
  const [mostrarTexto, setMostrarTexto] = useState(() => {
    if (notaAtual?.id) {
      const salvo = localStorage.getItem(getStorageKeyTexto(notaAtual.id));
      return salvo === null ? true : salvo === 'true';
    }
    return true;
  });

  // ── Controla se o texto pode ser editado (trava após salvar) ──
  const [textoEditavel, setTextoEditavel] = useState(true);

  const [criarObjetivosAtivo, setCriarObjetivosAtivo] = useState(false);
  const [buscaObjetivos, setBuscaObjetivos] = useState("");

  // ── Largura da coluna de objetivos (redimensionável) ──
  const [larguraObjetivos, setLarguraObjetivos] = useState(() => {
    const salvo = localStorage.getItem("ata_largura_objetivos");
    return salvo ? Number(salvo) : 600;
  });
  const [arrastandoDivisor, setArrastandoDivisor] = useState(false);
  const bodyRef = useRef(null);

  const cardRef                = useRef(null);
  const pdfDropdownRef         = useRef(null);
  const participantesSectionRef = useRef(null);
  const isEditingRef           = useRef(false);

  // ── Fechar ao clicar fora (junto dos outros useEffect de clique fora) ──
  useEffect(() => {
    const handle = (e) => { if (observacaoRef.current && !observacaoRef.current.contains(e.target)) setMostrarObservacaoInput(false); };
    if (mostrarObservacaoInput) { document.addEventListener("mousedown", handle); return () => document.removeEventListener("mousedown", handle); }
  }, [mostrarObservacaoInput]);

  // ── Persistir as escolhas no localStorage sempre que mudarem ──
  useEffect(() => {
    if (notaAtual?.id) {
      localStorage.setItem(getStorageKey(notaAtual.id), String(mostrarObjetivos));
    }
  }, [mostrarObjetivos, notaAtual?.id]);

  useEffect(() => {
    if (notaAtual?.id) {
      localStorage.setItem(getStorageKeyTexto(notaAtual.id), String(mostrarTexto));
    }
  }, [mostrarTexto, notaAtual?.id]);

  // ── Handler de arraste do divisor ──
  const handleDividerMouseDown = (e) => {
    e.preventDefault();
    setArrastandoDivisor(true);
  };

  useEffect(() => {
    if (!arrastandoDivisor) return;

    const handleMouseMove = (e) => {
      if (!bodyRef.current) return;
      const rect = bodyRef.current.getBoundingClientRect();
      const novaLargura = rect.right - e.clientX;
      const min = 320;
      const max = rect.width - 320;
      setLarguraObjetivos(Math.min(Math.max(novaLargura, min), max));
    };

    const handleMouseUp = () => {
      setArrastandoDivisor(false);
      localStorage.setItem("ata_largura_objetivos", String(larguraObjetivos));
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [arrastandoDivisor, larguraObjetivos]);

  // ── Navegação entre atas ───────────────────────────────────────────────────
  const navegarProxima = useCallback(() => {
    if (!atasDaPilha.length || indexAtual === -1) return;
    const proximoIndex = (indexAtual + 1) % atasDaPilha.length;
    const proximaAta = atasDaPilha[proximoIndex];
    if (proximaAta && onNavegarAta) {
      onNavegarAta(proximaAta);
    }
  }, [atasDaPilha, indexAtual, onNavegarAta]);

  const navegarAnterior = useCallback(() => {
    if (!atasDaPilha.length || indexAtual === -1) return;
    const anteriorIndex = (indexAtual - 1 + atasDaPilha.length) % atasDaPilha.length;
    const anteriorAta = atasDaPilha[anteriorIndex];
    if (anteriorAta && onNavegarAta) {
      onNavegarAta(anteriorAta);
    }
  }, [atasDaPilha, indexAtual, onNavegarAta]);

  // ── Teclado para navegação ────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      // ── Ignora também qualquer área contentEditable (ex: AtaTextoEditor) ──
      if (e.target.isContentEditable) return;
      if (e.target.closest && e.target.closest('[contenteditable="true"], .ata-textarea')) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        navegarProxima();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        navegarAnterior();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [navegarProxima, navegarAnterior]);

  // ── Detecta foco em input ───────────────────────────────────────────────────
  useEffect(() => {
    const handleFocusIn  = () => { if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') isEditingRef.current = true; };
    const handleFocusOut = () => { setTimeout(() => { const a = document.activeElement; if (!a || (a.tagName !== 'INPUT' && a.tagName !== 'TEXTAREA')) isEditingRef.current = false; }, 100); };
    document.addEventListener('focusin',  handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => { document.removeEventListener('focusin', handleFocusIn); document.removeEventListener('focusout', handleFocusOut); };
  }, []);

  // ── Fechar PDF dropdown ao clicar fora ─────────────────────────────────────
  useEffect(() => {
    const handle = (e) => { if (pdfDropdownRef.current && !pdfDropdownRef.current.contains(e.target)) setPdfDropdownOpen(false); };
    if (pdfDropdownOpen) { document.addEventListener("mousedown", handle); return () => document.removeEventListener("mousedown", handle); }
  }, [pdfDropdownOpen]);

  // ── Fechar input de participantes ao clicar fora ───────────────────────────
  useEffect(() => {
    const handle = (e) => { if (participantesSectionRef.current && !participantesSectionRef.current.contains(e.target)) { setShowParticipanteInput(false); setParticipanteInput(""); setSugestoesParticipantes([]); } };
    if (showParticipanteInput) { document.addEventListener("mousedown", handle); return () => document.removeEventListener("mousedown", handle); }
  }, [showParticipanteInput]);

  const fetchProjeto = useCallback(async () => {
  if (!projetoAtual?.id) return;
  if (projetoAtual.tipo === 'setor') {
    const { data } = await supabase.from("setores").select("name").eq("id", projetoAtual.id).single();
    setProjetoNome(data?.name || "Setor sem nome");
  } else {
    const { data } = await supabase.from("projects").select("name").eq("id", projetoAtual.id).single();
    setProjetoNome(data?.name || "Projeto sem nome");
  }
}, [projetoAtual]);

  const fetchUsuarioLogado = useCallback(async () => {
    const { data } = await supabase.auth.getUser();
    setUsuarioId(data?.user?.id || null);
  }, []);

  const fetchAta = useCallback(async () => {
    if (isEditingRef.current) return;
    if (!notaAtual?.id) { setLoading(false); return; }
    try {
      const { data: ata, error } = await supabase.from("atas").select("*").eq("nota_id", notaAtual.id).maybeSingle();
      if (error) { console.error("Erro ao carregar ata:", error); setLoading(false); return; }

      if (!ata) {
        const key = `rascunho_ata_${notaAtual.id}`;
        const rascunhoSalvo = localStorage.getItem(key);
        if (rascunhoSalvo) {
          try {
            const rascunho = JSON.parse(rascunhoSalvo);
            if (Date.now() - (rascunho.timestamp || 0) < 7 * 24 * 60 * 60 * 1000) {
              setAtaId(null); setPauta(rascunho.pauta || ""); setLocal(rascunho.local || "");
              setTexto(rascunho.texto || ""); setProxima(rascunho.proxima || "");
              setDataLocal(rascunho.dataLocal || ""); setParticipantes(rascunho.participantes || []);
              setObservacao(rascunho.observacao || "");
              setObservacaoDraft(rascunho.observacao || "");
              setObservacaoEditando(!rascunho.observacao?.trim());
              setAlteradoPorNome(""); setAlteradoEm(""); setAutorNome("Rascunho local");
              setTextoEditavel(true);
              setLoading(false); return;
            }
          } catch (e) { localStorage.removeItem(key); }
        }
        setAtaId(null); setPauta(""); setLocal(""); setTexto(""); setProxima("");
        setParticipantes([]); setDataLocal(""); setAlteradoPorNome(""); setAlteradoEm("");
        setObservacao(""); setObservacaoDraft(""); setObservacaoEditando(true);
        setTextoEditavel(true);
        setAutorNome("Ainda não redigida"); setLoading(false); return;
      }

      localStorage.removeItem(`rascunho_ata_${notaAtual.id}`);
      setAtaId(ata.id); setPauta(ata.pauta || ""); setLocal(ata.local || "");
      setTexto(ata.texto || ""); setProxima(ata.proxima_reuniao || ""); setDataLocal(ata.data_local || "");
      setObservacao(ata.observacao || "");
      setObservacaoDraft(ata.observacao || "");
      setObservacaoEditando(!ata.observacao?.trim());
      setTextoEditavel(false);

      const { data: partData } = await supabase.from("ata_participantes")
        .select("id, profile_id, nome_externo, funcao_externa, email_externo, profiles(id, nome, funcao)")
        .eq("ata_id", ata.id).order("id", { ascending: true });

      setParticipantes((partData || []).map(p => {
        if (p.profile_id) {
          const ok = p.profiles && typeof p.profiles === 'object' && p.profiles.nome;
          return ok 
            ? { 
                id: p.profiles.id || p.profile_id, 
                nome: formatarNomeExibicao(p.profiles.nome?.trim()), 
                nomeCompleto: p.profiles.nome?.trim() || "Usuário excluído",
                funcao: p.profiles.funcao?.trim() || "Membro" 
              }
            : { id: p.profile_id, nome: "Usuário excluído", nomeCompleto: "Usuário excluído", funcao: "Membro" };
        }
        const nomeExt = p.nome_externo?.trim() || "Convidado";
        return { 
          id: `ext-${p.id}`, 
          nome: formatarNomeExibicao(nomeExt), 
          nomeCompleto: nomeExt,
          funcao: p.funcao_externa?.trim() || "Externo",
          emailExterno: p.email_externo || "",
        };
      }).filter(p => p?.id));

      let nomeAutor = "Você";
      const criadorId = ata.redigido_por;
      if (criadorId) {
        const { data: p1 } = await supabase.from("profiles").select("nome").eq("id", criadorId).single();
        if (p1?.nome) nomeAutor = formatarNomeExibicao(p1.nome);
        else {
          const { data: p2 } = await supabase.from("profiles").select("nome").eq("user_id", criadorId).single();
          if (p2?.nome) nomeAutor = formatarNomeExibicao(p2.nome);
          else if (usuarioId) { const { data: mp } = await supabase.from("profiles").select("nome").eq("id", usuarioId).single(); nomeAutor = formatarNomeExibicao(mp?.nome) || "Você"; }
        }
      } else if (usuarioId) {
        const { data: mp } = await supabase.from("profiles").select("nome").eq("id", usuarioId).single();
        nomeAutor = formatarNomeExibicao(mp?.nome) || "Você";
      }
      setAutorNome(nomeAutor);

      if (ata.alterado_por) {
        const { data: pa } = await supabase.from("profiles").select("nome").eq("id", ata.alterado_por).single();
        let nome = pa?.nome || (await supabase.from("profiles").select("nome").eq("user_id", ata.alterado_por).single())?.data?.nome || "Usuário desconhecido";
        setAlteradoPorNome(formatarNomeExibicao(nome));
        if (ata.alterado_em) {
          const d = new Date(ata.alterado_em);
          setAlteradoEm(`${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} às ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`);
        }
      }
      setLoading(false);
    } catch (err) { console.error("Erro ao carregar ata:", err); setLoading(false); }
  }, [notaAtual?.id, usuarioId]);

  useEffect(() => { fetchProjeto(); fetchUsuarioLogado(); }, [fetchProjeto, fetchUsuarioLogado]);
  useEffect(() => { if (projetoAtual?.id && notaAtual?.id) fetchAta(); }, [projetoAtual?.id, notaAtual?.id, fetchAta]);

  // ── Auto-save rascunho ──
  useEffect(() => {
    if (!notaAtual?.id) return;
    const timer = setTimeout(() => {
      localStorage.setItem(`rascunho_ata_${notaAtual.id}`, JSON.stringify({ pauta, local, texto, proxima, dataLocal, participantes, observacao, timestamp: Date.now() }));
    }, 500);
    return () => clearTimeout(timer);
  }, [pauta, local, texto, proxima, dataLocal, participantes, observacao, notaAtual?.id]);

  // ── Funções de ação para observação ──
    const salvarObservacao = async () => {
    const valor = observacaoDraft;
    setObservacao(valor);
    setObservacaoEditando(false);

    if (!ataId) return; // ata ainda não existe, só fica local até o Salvar geral

    const { error } = await supabase
      .from("atas")
      .update({ observacao: valor, alterado_por: usuarioId, alterado_em: new Date().toISOString() })
      .eq("id", ataId);

    if (error) {
      console.error(error);
      alert("Erro ao salvar observação.");
    }
  };

  const cancelarObservacao = () => {
    setObservacaoDraft(observacao);
    setObservacaoEditando(false);
    if (!observacao.trim()) setMostrarObservacaoInput(false);
  };

  const excluirObservacao = () => {
    setObservacao("");
    setObservacaoDraft("");
    setObservacaoEditando(true);
  };

  // ── Participantes ──────────────────────────────────────────────────────────
  const handleParticipanteChange = async (e) => {
    const v = e.target.value;
    setParticipanteInput(v);
    if (v.startsWith("@") && v.length > 1 && containerAtual?.id) {
      const termo = v.slice(1).toLowerCase();
      try {
        const { data: convites } = await supabase.from("convites").select("user_id").eq("container_id", containerAtual.id).eq("status", "aceito");
        const userIds = (convites || []).map(c => c.user_id).filter(Boolean);
        if (!userIds.length) { setSugestoesParticipantes([]); return; }
        const { data: profiles } = await supabase.from("profiles").select("id, nickname, nome, funcao").in("id", userIds);
        const seen = new Set();
        setSugestoesParticipantes(
          (profiles || []).filter(p => (p.nickname?.toLowerCase().includes(termo) || p.nome?.toLowerCase().includes(termo)) && !seen.has(p.id) && seen.add(p.id)).slice(0, 10)
        );
      } catch { setSugestoesParticipantes([]); }
    } else { setSugestoesParticipantes([]); }
  };

  const selecionarSugestao = (item) => {
    if (!participantes.some(p => p.id === item.id))
      setParticipantes(prev => [...prev, { 
        id: item.id, 
        nome: formatarNomeExibicao(item.nome), 
        nomeCompleto: item.nome,
        funcao: item.funcao || "Membro" 
      }]);
    setParticipanteInput(""); setSugestoesParticipantes([]); setShowParticipanteInput(false);
  };

  const adicionarParticipanteExterno = () => {
    if (participanteInput.trim()) {
      const nomeExt = participanteInput.trim();
      setParticipantes(prev => [...prev, { 
        id: `ext-${extIdCounter}`, 
        nome: formatarNomeExibicao(nomeExt), 
        nomeCompleto: nomeExt,
        funcao: "Externo" 
      }]);
      setParticipanteInput(""); setExtIdCounter(prev => prev + 1); setShowParticipanteInput(false);
    }
  };

  const removerParticipante = (id) => setParticipantes(prev => prev.filter(p => p.id !== id));

  // ── Exportar PDF ──────────────────────────────────────────────────────────
  const exportarPdf = async (opcao) => {
    setPdfDropdownOpen(false);
    if (!projetoNome || !notaAtual?.nome) {
      alert("Não é possível exportar PDF: projeto ou nota não identificados.");
      return;
    }

    let objetivosAtuais = [];

    if (opcao === 'objetivos' || opcao === 'completo') {
      if (!ataId) {
        alert("Salve a ata antes de exportar objetivos.");
        return;
      }
      try {
        const { data: od, error } = await supabase
          .from("ata_objetivos")
          .select("*")
          .eq("ata_id", ataId)
          .order("ordem", { ascending: true })
          .order("id", { ascending: true });

        if (error) throw error;

        if (od?.length) {
          const ids = od.map(o => o.id);
          const { data: rd } = await supabase
            .from("ata_objetivos_responsaveis_enriquecidos")
            .select("*")
            .in("ata_objetivo_id", ids);

          const respPorObj = (rd || []).reduce((acc, r) => {
            if (!acc[r.ata_objetivo_id]) acc[r.ata_objetivo_id] = [];
            acc[r.ata_objetivo_id].push({
              id: r.id,
              usuario_id: r.usuario_id,
              nome_externo: r.nome_externo,
              nome_exibicao: r.nome_exibicao
            });
            return acc;
          }, {});

          // ── Buscar comentários dos objetivos ──
          const { data: comentariosData } = await supabase
            .from("ata_objetivos_comentarios")
            .select("*")
            .in("ata_objetivo_id", ids)
            .order("created_at", { ascending: true });

          const userIdsComentarios = [...new Set((comentariosData || []).map(c => c.usuario_id))];
          let nomesMap = {};
          if (userIdsComentarios.length) {
            const { data: profilesComentarios } = await supabase
              .from("profiles")
              .select("id, nome")
              .in("id", userIdsComentarios);
            nomesMap = (profilesComentarios || []).reduce((acc, p) => { acc[p.id] = p.nome; return acc; }, {});
          }

          const comentariosPorObj = (comentariosData || []).reduce((acc, c) => {
            if (!acc[c.ata_objetivo_id]) acc[c.ata_objetivo_id] = [];
            acc[c.ata_objetivo_id].push({
              autor: nomesMap[c.usuario_id] || "Usuário",
              texto: c.comentario,
              data: c.created_at
            });
            return acc;
          }, {});

          objetivosAtuais = od
            .filter(o => !o.texto?.startsWith('[EXCLUIDO]'))
            .map(o => ({
              id: o.id,
              texto: o.texto || "",
              responsaveis: respPorObj[o.id] || [],
              dataEntrega: o.data_entrega,
              concluido: o.concluido || false,
              concluidoEm: o.concluido_em ? new Date(o.concluido_em) : null,
              comentario: o.comentario || "",
              comentarios: comentariosPorObj[o.id] || []
            }));
        }

        if (!objetivosAtuais.length) {
          alert("Nenhum objetivo encontrado para exportar.");
          return;
        }
      } catch (err) {
        console.error("Erro ao buscar objetivos para PDF:", err);
        alert("Erro ao buscar objetivos.");
        return;
      }
    }

    console.log('objetivosAtuais para PDF:', objetivosAtuais.length, objetivosAtuais);

      await AtaPdf.exportar(
        opcao, projetoNome, notaAtual.nome, dataLocal,
        pauta, local, texto, participantes, objetivosAtuais, observacao
      );
  };

  // ── Salvar ata ──
  const salvarAta = useCallback(async () => {
    if (!usuarioId || !notaAtual?.id || !projetoAtual?.id || !projetoAtual?.tipo) { alert("Dados insuficientes."); return; }
    if (projetoAtual.tipo !== 'projeto' && projetoAtual.tipo !== 'setor') { alert("Tipo inválido."); return; }
    setSalvando(true); setSalvoComSucesso(false);
    try {
      const agora = new Date().toISOString();
      const payloadBase = { nota_id: notaAtual.id, pauta, local, texto, proxima_reuniao: proxima || null, data_local: dataLocal, observacao, alterado_por: usuarioId, alterado_em: agora };
      const payload = projetoAtual.tipo === 'projeto' ? { ...payloadBase, projeto_id: projetoAtual.id, setor_id: null } : { ...payloadBase, projeto_id: null, setor_id: projetoAtual.id };

      let savedAta;
      if (ataId) {
        const { data, error } = await supabase.from("atas").update(payload).eq("id", ataId).select().single();
        if (error) throw error;
        savedAta = data;
      } else {
        const { data, error } = await supabase.from("atas").insert([{ ...payload, redigido_por: usuarioId, criado_em: agora }]).select().single();
        if (error) throw error;
        savedAta = data; setAtaId(savedAta.id); setAutorNome("Carregando...");
      }

      await supabase.from("ata_participantes").delete().eq("ata_id", savedAta.id);
      for (const p of participantes) {
        if (p.id.toString().startsWith("ext")) {
          await supabase.from("ata_participantes").insert({
            ata_id: savedAta.id,
            nome_externo: p.nomeCompleto || p.nome,
            funcao_externa: p.funcao,
            email_externo: p.emailExterno || null,
          });
        } else {
          await supabase.from("ata_participantes").insert({ ata_id: savedAta.id, profile_id: p.id });
        }
      }

      setSalvoComSucesso(true);
      setTextoEditavel(false);
      localStorage.removeItem(`rascunho_ata_${notaAtual.id}`);
      setTimeout(() => setSalvoComSucesso(false), 2000);
    } catch (e) { console.error(e); alert(`❌ Erro ao salvar ata: ${e.message}`); }
    finally { setSalvando(false); }
  }, [ataId, usuarioId, notaAtual, projetoAtual, pauta, local, texto, proxima, dataLocal, participantes, observacao]);

  if (loading) {
    return (
      <div className="ata-modal" ref={cardRef}>
        <div className="task-loading-container"><Loading size={200} /></div>
      </div>
    );
  }

  const notaNome    = notaAtual?.nome || "Sem nota";
  const projetoExib = projetoNome || projetoAtual?.nome || projetoAtual?.name || "Projeto";
  const temMultiplasAtas = atasDaPilha.length > 1;

  return (
    <div className={`ata-modal${telaCheia ? " ata-modal--tela-cheia" : ""}`} ref={cardRef}>

      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <div className="task-header">
        <div className="task-header-left">
          <div className="task-header-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <div className="task-header-titles">
            <span className="task-header-title">{notaNome}</span>
            <span className="task-header-sub">{projetoExib}</span>
          </div>
        </div>
        <div className="task-header-right" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* ── Navegação entre atas ── */}
          {temMultiplasAtas && (
            <div className="ata-navegacao-botoes" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                className="ata-nav-btn"
                onClick={navegarAnterior}
                title="Ata anterior (←)"
                disabled={atasDaPilha.length <= 1}
              >
                <FaChevronLeft size={14} />
              </button>
              <span className="ata-nav-indice" style={{ fontSize: 12, color: '#6b7280', minWidth: 40, textAlign: 'center' }}>
                {indexAtual + 1}/{atasDaPilha.length}
              </span>
              <button
                className="ata-nav-btn"
                onClick={navegarProxima}
                title="Próxima ata (→)"
                disabled={atasDaPilha.length <= 1}
              >
                <FaChevronRight size={14} />
              </button>
            </div>
          )}
          {ultimaAlteracao && <span className="ata-ultima-alteracao">{ultimaAlteracao}</span>}
          <button
            className="task-close-btn"
            onClick={() => setShowEnviarEmail(true)}
            title="Enviar ata por e-mail"
            aria-label="Enviar por e-mail"
          >
            <FaEnvelope />
          </button>
          <button
            className="task-close-btn"
            onClick={() => setTelaCheia((v) => !v)}
            title={telaCheia ? "Restaurar modal" : "Tela cheia"}
            aria-label={telaCheia ? "Restaurar modal" : "Tela cheia"}
          >
            {telaCheia ? <FaCompress /> : <FaExpand />}
          </button>
          {onClose && (
            <button className="task-close-btn" onClick={onClose} aria-label="Fechar">
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {/* ══ BODY com componentes sempre montados ════════════════════════════ */}
      <div
        className="task-body"
        ref={bodyRef}
        style={{ display: 'flex', flexDirection: 'row', height: 'calc(100% - 64px)', overflow: 'hidden' }}
      >
        {/* ── Trilho esquerdo: reabre o Texto quando ele está oculto ── */}
        <button
          className="ata-rail ata-rail--left"
          onClick={() => setMostrarTexto(true)}
          title="Mostrar texto"
          style={{ display: !mostrarTexto ? 'flex' : 'none' }}
        >
          <FaChevronRight size={12} />
        </button>

        {/* ── Estado vazio ── */}
        <div
          style={{
            display: (!mostrarTexto && !mostrarObjetivos) ? 'flex' : 'none',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', flex: 1, gap: 12, color: '#9ca3af', textAlign: 'center', padding: '0 24px'
          }}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div style={{ fontSize: 14, fontWeight: 500 }}>Nada para exibir</div>
          <div style={{ fontSize: 13 }}>Use as setas laterais para mostrar o texto ou os objetivos.</div>
        </div>

        {/* ── COLUNA TEXTO — sempre montada ── */}
        <div
          className="task-col-left"
          style={{
            display: mostrarTexto ? 'flex' : 'none',
            flexDirection: 'column', height: '100%', overflow: 'hidden',
            flex: 1, minWidth: 0
          }}
        >
          {/* Pauta + local + data */}
          <div className="ata-meta-row" style={{ flexShrink: 0 }}>
            <div className="ata-meta-pauta">
              {editing.pauta ? (
                <input
                  className="ata-meta-input ata-meta-input--pauta"
                  value={pauta}
                  onChange={e => setPauta(e.target.value)}
                  onBlur={() => setEditing(p => ({ ...p, pauta: false }))}
                  onKeyDown={e => e.key === "Enter" && setEditing(p => ({ ...p, pauta: false }))}
                  autoFocus
                  placeholder="Pauta da reunião"
                />
              ) : (
                <h2
                  className={`ata-meta-pauta-text${!pauta ? ' ata-meta-placeholder' : ''}`}
                  onDoubleClick={() => setEditing(p => ({ ...p, pauta: true }))}
                  title="Duplo clique para editar"
                >
                  {pauta || "Pauta da reunião"}
                </h2>
              )}
            </div>

            <div className="ata-meta-linha">
              <div className="ata-meta-field">
                {editing.local ? (
                  <input
                    className="ata-meta-input"
                    value={local}
                    onChange={e => setLocal(e.target.value)}
                    onBlur={() => setEditing(p => ({ ...p, local: false }))}
                    onKeyDown={e => e.key === "Enter" && setEditing(p => ({ ...p, local: false }))}
                    autoFocus
                    placeholder="Local"
                  />
                ) : (
                  <span
                    className={`ata-meta-text${!local ? ' ata-meta-placeholder' : ''}`}
                    onDoubleClick={() => setEditing(p => ({ ...p, local: true }))}
                  >
                    {local || "Local"}
                  </span>
                )}
              </div>

              <span className="ata-meta-sep">·</span>

              <div className="ata-meta-field">
                {editingDataLocal ? (
                  <input
                    className="ata-meta-input"
                    value={dataLocal}
                    onChange={e => setDataLocal(e.target.value)}
                    onBlur={() => setEditingDataLocal(false)}
                    onKeyDown={e => e.key === "Enter" && setEditingDataLocal(false)}
                    autoFocus
                    placeholder="Cidade, DD de Mês de AAAA"
                  />
                ) : (
                  <span
                    className={`ata-meta-text${!dataLocal ? ' ata-meta-placeholder' : ''}`}
                    onDoubleClick={() => setEditingDataLocal(true)}
                  >
                    {dataLocal || "Cidade e Data"}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="task-divider" style={{ flexShrink: 0 }} />

          {/* Participantes */}
          <div className="ata-participantes-row" style={{ flexShrink: 0 }}>
            <div className="section-label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span>Integrantes</span>
              <span className="ata-contador">({participantes.length})</span>
              <button
                className="ata-btn-add-participante"
                onClick={() => setShowParticipanteInput(true)}
                title="Adicionar participante"
              >
                <FaUserPlus size={12} />
              </button>
            </div>

            <div ref={participantesSectionRef} className="ata-participantes-body">
              {showParticipanteInput && (
                <div className="ata-participante-input-wrap">
                  <input
                    type="text"
                    value={participanteInput}
                    onChange={handleParticipanteChange}
                    placeholder="@nickname ou nome externo + Enter"
                    className="ata-participante-input"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        sugestoesParticipantes.length > 0 ? selecionarSugestao(sugestoesParticipantes[0]) : adicionarParticipanteExterno();
                      }
                    }}
                    onBlur={() => { setTimeout(() => { if (!sugestoesParticipantes.length) { setShowParticipanteInput(false); setParticipanteInput(""); } }, 200); }}
                  />
                  {sugestoesParticipantes.length > 0 && (
                    <div className="ata-sugestoes-lista">
                      {sugestoesParticipantes.map(item => (
                        <div key={item.id} className="ata-sugestao-item" onClick={() => selecionarSugestao(item)}>
                          <span>@{item.nickname || item.nome}</span>
                          <span className="ata-sugestao-funcao">{item.funcao}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="ata-avatares-participantes">
                {participantes.filter(p => p && typeof p === 'object').map(p => (
                  <div key={p.id} className="ata-avatar-wrapper">
                    <div className="ata-avatar-circle">
                      {(p.nomeCompleto || p.nome || '?').trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="ata-avatar-info">
                      <span className="ata-avatar-nome">{p.nomeCompleto || p.nome}</span>
                      <span className="ata-avatar-funcao">{p.funcao}</span>
                    </div>
                    <button
                      type="button"
                      className="ata-avatar-remover"
                      onClick={(e) => { e.stopPropagation(); removerParticipante(p.id); }}
                      aria-label={`Remover ${p.nomeCompleto || p.nome}`}
                      title="Remover"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="task-divider" style={{ flexShrink: 0 }} />

          {/* Texto da ata */}
          <div className="descricao-section" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', padding: '0 24px' }}>
            <div className="section-label" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                  <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
                  <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
                <span>Texto</span>
              </span>
            </div>

            <AtaTextoEditor
              value={texto}
              onChange={setTexto}
              placeholder="Digite o texto da ata..."
              className="ata-textarea"
              disabled={!textoEditavel}
            />
          </div>

          <div className="task-divider" style={{ flexShrink: 0 }} />

          {/* Rodapé */}
          <div className="ata-rodape" style={{ 
            marginTop: 'auto', 
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
          }}>
            <div className="ata-rodape-esquerda">
              <div className="ata-proxima-linha">
                <label className="ata-proxima-label">Próxima reunião:</label>
                <input
                  type="date"
                  value={proxima}
                  onChange={e => setProxima(e.target.value)}
                  className="ata-proxima-input"
                />
              </div>
              {ataId && (
                <div className="ata-autor-info">
                  <span>Redigida por <strong>{autorNome}</strong></span>
                  {alteradoPorNome && alteradoEm && (
                    <span> · Alterada por <strong>{alteradoPorNome}</strong> em <strong>{alteradoEm}</strong></span>
                  )}
                </div>
              )}
            </div>

            <div className="ata-rodape-acoes" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}>
              {/* ── Botão Observação (antes de Objetivos) ── */}
              <div className="ata-observacao-container" ref={observacaoRef} style={{ position: 'relative' }}>
                <button
                  className={`ata-btn-observacao${observacao.trim() ? ' ativo' : ''}`}
                  onClick={() => {
                    setObservacaoDraft(observacao);
                    setObservacaoEditando(!observacao.trim());
                    setMostrarObservacaoInput(prev => !prev);
                  }}
                  title="Observação"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: '1px solid #d0d5dd',
                    background: observacao.trim() ? '#fef9c3' : '#fff',
                    color: observacao.trim() ? '#a16207' : '#6b7280',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>Observação</span>
                </button>
                {mostrarObservacaoInput && (
                  <div className="ata-observacao-dropdown">
                    {observacaoEditando ? (
                      <>
                        <textarea
                          className="ata-observacao-textarea"
                          value={observacaoDraft}
                          onChange={e => setObservacaoDraft(e.target.value)}
                          placeholder="Digite uma observação..."
                          autoFocus
                        />
                        <div className="ata-observacao-acoes" style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button 
                            className="ata-observacao-btn ata-observacao-btn--salvar" 
                            onClick={salvarObservacao}
                          >
                            Salvar
                          </button>
                          <button 
                            className="ata-observacao-btn ata-observacao-btn--cancelar" 
                            onClick={cancelarObservacao}
                          >
                            Cancelar
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="ata-observacao-texto">
                          {observacao}
                        </div>
                        <div className="ata-observacao-acoes" style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button 
                            className="ata-observacao-btn ata-observacao-btn--editar" 
                            onClick={() => setObservacaoEditando(true)}
                          >
                            Editar
                          </button>
                          <button 
                            className="ata-observacao-btn ata-observacao-btn--excluir" 
                            onClick={excluirObservacao}
                          >
                            Excluir
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button
                className={`ata-btn-objetivos-toggle${mostrarObjetivos ? ' ativo' : ''}`}
                onClick={() => setMostrarObjetivos(prev => !prev)}
                title={mostrarObjetivos ? "Ocultar objetivos" : "Mostrar objetivos"}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: '1px solid #d0d5dd',
                  background: mostrarObjetivos ? '#ecfdf5' : '#fff',
                  color: mostrarObjetivos ? '#059669' : '#6b7280',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = mostrarObjetivos ? '#d1fae5' : '#f3f4f6'; }}
                onMouseLeave={e => { e.currentTarget.style.background = mostrarObjetivos ? '#ecfdf5' : '#fff'; }}
              >
                <span>Objetivos</span>
              </button>

              <div className="ata-rodape-divisor" style={{ width: '1px', height: '24px', background: '#e5e7eb' }} />

              <div className="ata-rodape-grupo-salvar" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button className="ata-btn-salvar" onClick={salvarAta} disabled={salvando} style={{
                  padding: '6px 18px',
                  borderRadius: '6px',
                  border: 'none',
                  background: salvando ? '#9ca3af' : '#3b82f6',
                  color: '#fff',
                  cursor: salvando ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.15s ease',
                }}>
                  {salvando ? "Salvando…" : "Salvar"}
                </button>
                {!textoEditavel && (
                  <button 
                    className="ata-btn-editar" 
                    onClick={() => setTextoEditavel(true)} 
                    title="Editar texto"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d0d5dd',
                      background: '#fff',
                      color: '#1565c0',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                  >
                    <span>Editar</span>
                  </button>
                )}
                {salvoComSucesso && <span className="ata-salvo-ok" style={{ color: '#059669', fontSize: '12px', fontWeight: '500' }}>Salvo!</span>}
              </div>

              <div className="ata-rodape-divisor" style={{ width: '1px', height: '24px', background: '#e5e7eb' }} />

              <div className="ata-pdf-container" ref={pdfDropdownRef} style={{ position: 'relative' }}>
                <button 
                  className="ata-btn-pdf" 
                  onClick={() => setPdfDropdownOpen(prev => !prev)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: '1px solid #d0d5dd',
                    background: '#fff',
                    color: '#6b7280',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                >
                  PDF
                </button>
                {pdfDropdownOpen && (
                  <div className="ata-pdf-dropdown" style={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: '4px',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    minWidth: '180px',
                    zIndex: 10,
                    overflow: 'hidden',
                  }}>
                    <button 
                      onClick={() => exportarPdf('texto')} 
                      className="ata-pdf-option"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 16px',
                        border: 'none',
                        background: 'transparent',
                        color: '#374151',
                        cursor: 'pointer',
                        fontSize: '13px',
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Texto da ata
                    </button>
                    <button 
                      onClick={() => exportarPdf('objetivos')} 
                      className="ata-pdf-option"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 16px',
                        border: 'none',
                        background: 'transparent',
                        color: '#374151',
                        cursor: 'pointer',
                        fontSize: '13px',
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Apenas Objetivos
                    </button>
                    <button 
                      onClick={() => exportarPdf('completo')} 
                      className="ata-pdf-option ata-pdf-option--highlight"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 16px',
                        border: 'none',
                        background: 'transparent',
                        color: '#1565c0',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Texto e Objetivos
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── DIVISOR COM SETAS (arrastável) ── */}
        <div
          className={`ata-col-divider${arrastandoDivisor ? " ata-col-divider--arrastando" : ""}`}
          style={{ display: (mostrarTexto && mostrarObjetivos) ? 'flex' : 'none' }}
          onMouseDown={handleDividerMouseDown}
        >
          <button
            className="ata-divider-arrow"
            onClick={() => setMostrarTexto(false)}
            title="Ocultar texto e expandir objetivos"
          >
            <FaChevronLeft size={10} />
          </button>
          <div className="ata-col-divider-handle" />
          <button
            className="ata-divider-arrow"
            onClick={() => setMostrarObjetivos(false)}
            title="Ocultar objetivos e expandir texto"
          >
            <FaChevronRight size={10} />
          </button>
        </div>

        {/* ── COLUNA OBJETIVOS — sempre montada ── */}
        <div
          className="task-col-right ata-col-objetivos"
          style={{
            display: mostrarObjetivos ? 'flex' : 'none',
            flexDirection: 'column', minHeight: 0, overflow: 'hidden',
            flex: mostrarTexto ? `0 0 ${larguraObjetivos}px` : 1,
            borderLeft: mostrarTexto ? '1px solid #e5e7eb' : 'none'
          }}
        >
          <div className="ata-objetivos-header-label">
            <input
              type="checkbox"
              id="ata-criar-objetivos-toggle"
              checked={criarObjetivosAtivo}
              onChange={e => {
                if (e.target.checked) setCriarObjetivosAtivo(true);
              }}
              className="ata-objetivos-toggle-check"
            />
            <label htmlFor="ata-criar-objetivos-toggle" className="ata-objetivos-toggle-label">
              Criar objetivos
            </label>
            <div className="ata-objetivos-info-wrapper">
              <span className="ata-objetivos-info-btn" tabIndex={0} aria-label="Como criar objetivos">ⓘ</span>
              <div className="ata-objetivos-info-tooltip">
                Ao marcar esta opção, você poderá criar objetivos a partir do texto da ata.<br/>
                Basta colocar as frases entre <strong>aspas simples</strong> — ex: <em>'Revisar contrato até sexta'</em> — e elas virarão objetivos automaticamente.
              </div>
            </div>

            {/* ── NOVO: busca de objetivos ── */}
            {criarObjetivosAtivo && (
              <div className="ata-busca-objetivos-wrap">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  value={buscaObjetivos}
                  onChange={e => setBuscaObjetivos(e.target.value)}
                  placeholder="Buscar objetivo..."
                  className="ata-busca-objetivos-input"
                />
                {buscaObjetivos && (
                  <button
                    className="ata-busca-objetivos-limpar"
                    onClick={() => setBuscaObjetivos("")}
                    title="Limpar busca"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </div>

          <AtaObjetivos
            ataId={ataId}
            texto={texto}
            usuarioId={usuarioId}
            projetoAtual={projetoAtual}
            notaAtual={notaAtual}
            projetoNome={projetoNome}
            autorNome={autorNome}
            onProgressoChange={onProgressoChange}
            containerAtual={containerAtual}
            onOpenTask={onOpenTask}
            criarObjetivosExterno={criarObjetivosAtivo}
            onCriarObjetivosChange={setCriarObjetivosAtivo}
            buscaObjetivos={buscaObjetivos}
          />
        </div>

        {/* ── Trilho direito: reabre Objetivos quando oculto ── */}
        <button
          className="ata-rail ata-rail--right"
          onClick={() => setMostrarObjetivos(true)}
          title="Mostrar objetivos"
          style={{ display: !mostrarObjetivos ? 'flex' : 'none' }}
        >
          <FaChevronLeft size={12} />
        </button>
      </div>

      {showEnviarEmail && (
        <EnviarAtaEmailModal
          ataId={ataId}
          notaNome={notaNome}
          projetoNome={projetoExib}
          participantes={participantes}
          dadosAta={{ dataLocal, pauta, local, texto, observacao }}
          onClose={() => setShowEnviarEmail(false)}
        />
      )}
    </div>
  );
}